/**
 * Input select type
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
/**
 * Material UI
 */
import { makeStyles } from '@mui/styles';
/**
 * Helpers
 */
import Utils from '../../helpers/utils';

const useStyles = makeStyles(() => ({
  isInvalid: {
    '& > input,select': {
      borderColor: '#d50000',
    },
    '& > .mdl-textfield__error': {
      visibility: 'visible',
    },
  },
}));

/**
 * InputText
 *
 * @param debounceTime {number} Time after to call formik update
 * @param items {array} values to print in select option
 * @param label {string} Label of input text
 * @param name {string} key of formik.values to get value
 * @param errorMessage {string} Error message to print
 * @param formik {object} the formik object passed as prop
 * @param selectClassName {string} Class to apply to select
 * @param style {object} Style to apply to field
 * @param disabled {boolean} Disabled input field
 *
 * @returns {JSX.Element}
 */
function InputSelect({
  debounceTime,
  items,
  label,
  name,
  errorMessage,
  formik,
  selectClassName,
  style,
  disabled,
}) {
  const classes = useStyles();
  const [value, setValue] = useState('');
  const onDebouncedChange = Utils.useDebouncedCallback(debounceTime);

  // Update value if formik object changes
  useEffect(() => {
    const formikFieldValue = formik.values[name];
    if (formikFieldValue !== value) {
      setValue(formikFieldValue);
    }
  }, [formik.values]);

  return (
    <div className={Utils.getFieldClasses(formik, name, classes, true)} style={style}>
      <select
        id={name}
        name={name}
        className={selectClassName}
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
          onDebouncedChange(() => {
            formik.handleChange(event);
          });
        }}
        onBlur={formik.handleBlur}
        disabled={disabled}
      >
        <option value="">{label}</option>
        {items.length && items.map((item) => (
          <option key={uuid()} value={item.value}>
            {item.text}
          </option>
        ))}
      </select>
      <span className="mdl-textfield__error">{errorMessage}</span>
    </div>
  );
}
InputSelect.defaultProps = {
  debounceTime: 200,
  items: [],
  label: '',
  errorMessage: '',
  selectClassName: 'ga-select',
  style: {},
  disabled: false,
};
InputSelect.propTypes = {
  debounceTime: PropTypes.number,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  formik: PropTypes.shape({
    values: PropTypes.shape({}).isRequired,
    handleChange: PropTypes.func.isRequired,
    handleBlur: PropTypes.func.isRequired,
  }).isRequired,
  errorMessage: PropTypes.string,
  selectClassName: PropTypes.string,
  style: PropTypes.shape({}),
  disabled: PropTypes.bool,
};

export default InputSelect;
